import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

export interface EmailPreviewData {
  id: string;
  to: string[];
  cc: string[];
  bcc?: string[];
  subject: string;
  htmlContent: string;
  plainTextContent?: string;
  selected: boolean;
  metadata?: any;
  workerInfo?: {
    name: string;
    email: string;
    phone?: string;
  };
}

export interface EmailConfirmationResult {
  confirmed: boolean;
  emails: EmailPreviewData[];
  action: 'send' | 'cancel';
}

@Component({
  selector: 'app-email-confirmation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './email-confirmation-modal.component.html',
  styleUrls: ['./email-confirmation-modal.component.scss']
})
export class EmailConfirmationModalComponent implements OnInit {
  @Input() show: boolean = false;
  @Input() emails: EmailPreviewData[] = [];
  @Input() title: string = '寄信確認';
  @Input() constructionInfo: any = null;
  @Input() showConstructionInfo: boolean = false; // 🆕 控制是否顯示黃色施工資訊區塊（預設不顯示）

  @Output() confirmed = new EventEmitter<EmailConfirmationResult>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() modalClosed = new EventEmitter<void>();

  // UI 控制
  currentEmailIndex: number = 0;
  showPreview: boolean = false;
  selectAll: boolean = true;

  // 編輯模式
  editingEmail: EmailPreviewData | null = null;
  originalEmail: EmailPreviewData | null = null;
  
  // 收件人文字框
  toRecipientsText: string = '';
  ccRecipientsText: string = '';

  constructor(private i18nService: I18nService) {}

  ngOnInit(): void {
    // 預設全選所有郵件
    this.updateSelectAllState();
  }

  /**
   * 取得已選取的郵件數量
   */
  get selectedEmailsCount(): number {
    return this.emails.filter(email => email.selected).length;
  }

  /**
   * 取得總郵件數量
   */
  get totalEmailsCount(): number {
    return this.emails.length;
  }

  /**
   * 全選/取消全選
   */
  onSelectAllChange(): void {
    this.emails.forEach(email => {
      email.selected = this.selectAll;
    });
  }

  /**
   * 更新全選狀態
   */
  updateSelectAllState(): void {
    const selectedCount = this.selectedEmailsCount;
    const totalCount = this.totalEmailsCount;
    
    if (selectedCount === 0) {
      this.selectAll = false;
    } else if (selectedCount === totalCount) {
      this.selectAll = true;
    } else {
      // 部分選取狀態
      this.selectAll = false;
    }
  }

  /**
   * 當個別郵件選擇狀態改變時
   */
  onEmailSelectionChange(): void {
    this.updateSelectAllState();
  }

  /**
   * 開始編輯郵件
   */
  startEditEmail(email: EmailPreviewData): void {
    this.editingEmail = { ...email };
    this.originalEmail = email;
    
    // 將陣列格式轉換為逗號分隔的文字
    this.toRecipientsText = email.to.join(', ');
    this.ccRecipientsText = email.cc.join(', ');
  }

  /**
   * 儲存郵件編輯
   */
  saveEmailEdit(): void {
    if (this.editingEmail && this.originalEmail) {
      // 將逗號分隔的文字轉換回陣列格式
      this.editingEmail.to = this.parseEmailList(this.toRecipientsText);
      this.editingEmail.cc = this.parseEmailList(this.ccRecipientsText);
      
      // 更新原始郵件資料
      Object.assign(this.originalEmail, this.editingEmail);
      
      this.editingEmail = null;
      this.originalEmail = null;
      this.toRecipientsText = '';
      this.ccRecipientsText = '';
    }
  }

  /**
   * 取消郵件編輯
   */
  cancelEmailEdit(): void {
    this.editingEmail = null;
    this.originalEmail = null;
    this.toRecipientsText = '';
    this.ccRecipientsText = '';
  }

  /**
   * 解析郵件清單字串（逗號分隔）為陣列
   */
  private parseEmailList(emailString: string): string[] {
    if (!emailString || emailString.trim() === '') {
      return [];
    }
    
    return emailString
      .split(',')
      .map(email => email.trim())
      .filter(email => email !== '' && this.isValidEmail(email));
  }

  /**
   * 簡單的信箱格式驗證
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 預覽郵件內容
   */
  previewEmail(email: EmailPreviewData): void {
    this.currentEmailIndex = this.emails.findIndex(e => e.id === email.id);
    this.showPreview = true;
  }

  /**
   * 關閉預覽
   */
  closePreview(): void {
    this.showPreview = false;
  }

  /**
   * 上一封郵件
   */
  previousEmail(): void {
    if (this.currentEmailIndex > 0) {
      this.currentEmailIndex--;
    }
  }

  /**
   * 下一封郵件
   */
  nextEmail(): void {
    if (this.currentEmailIndex < this.emails.length - 1) {
      this.currentEmailIndex++;
    }
  }

  /**
   * 取得當前預覽的郵件
   */
  get currentPreviewEmail(): EmailPreviewData | null {
    if (this.currentEmailIndex >= 0 && this.currentEmailIndex < this.emails.length) {
      return this.emails[this.currentEmailIndex];
    }
    return null;
  }

  /**
   * 確認發送
   */
  confirmSend(): void {
    const selectedEmails = this.emails.filter(email => email.selected);
    
    if (selectedEmails.length === 0) {
      alert('請至少選擇一封郵件進行發送！');
      return;
    }

    // 用戶已經在彈窗中確認，不需要再次確認
    const result: EmailConfirmationResult = {
      confirmed: true,
      emails: selectedEmails,
      action: 'send'
    };
    
    console.log('✅ 用戶確認發送郵件，選中的郵件數量：', selectedEmails.length);
    this.confirmed.emit(result);
    this.closeModal();
  }

  /**
   * 取得收件人預覽
   */
  private getRecipientsPreview(emails: EmailPreviewData[]): string {
    const allRecipients = new Set<string>();
    
    emails.forEach(email => {
      email.to.forEach(to => allRecipients.add(to));
      email.cc.forEach(cc => allRecipients.add(cc));
    });

    const recipients = Array.from(allRecipients);
    if (recipients.length > 5) {
      return recipients.slice(0, 5).join('\n') + `\n...等共 ${recipients.length} 人`;
    }
    return recipients.join('\n');
  }

  /**
   * 取消發送
   */
  cancelSend(): void {
    console.log('❌ 用戶取消發送郵件');
    this.cancelled.emit();
    this.closeModal();
  }

  /**
   * 關閉彈窗
   */
  closeModal(): void {
    this.show = false;
    this.showPreview = false;
    this.editingEmail = null;
    this.originalEmail = null;
    this.modalClosed.emit();
  }

  /**
   * ESC 鍵關閉彈窗
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.showPreview) {
        this.closePreview();
      } else if (this.editingEmail) {
        this.cancelEmailEdit();
      } else {
        this.closeModal();
      }
    }
  }

  /**
   * 取得郵件類型標籤
   */
  getEmailTypeLabel(email: EmailPreviewData): string {
    if (email.metadata?.emailType === 'personalized_worker_notification') {
      return '個人化通知';
    } else if (email.metadata?.emailType === 'contractor_notification') {
      return '承攬商通知';
    }
    return '一般通知';
  }

  /**
   * 取得郵件狀態顏色
   */
  getEmailStatusClass(email: EmailPreviewData): string {
    if (!email.selected) return 'email-item-disabled';
    if (email.metadata?.emailType === 'personalized_worker_notification') {
      return 'email-item-worker';
    } else if (email.metadata?.emailType === 'contractor_notification') {
      return 'email-item-contractor';
    }
    return 'email-item-general';
  }
}